import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzAlgDataService,
  BokkzBtwDataService,
  BtwOverzicht,
  BtwOverzichtFrequentie,
  ValueAddedTaxEntity
} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormControl, FormGroup} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {debounceTime, filter, merge, Observable, shareReplay, tap} from 'rxjs';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AsyncPipe} from "@angular/common";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-algemeen',
  templateUrl: './algemeen.component.html',
  styleUrls: ['./algemeen.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    AsyncPipe
  ]
})
export class AlgemeenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private readonly destroyRef = inject(DestroyRef);
  private readonly algDataService = inject(BokkzAlgDataService);
  private readonly dataService = inject(BokkzBtwDataService);
  public beconNummer = '';
  public btwNummer = '';

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: 'Ja'},
    {value: false, label: 'Nee'},
  ];

  public btwOverzichtFg = new FormGroup({
    beconNummer: new FormControl<string | null>(null),
    initialenBelastingplichtige: new FormControl<string | null>(null),
    tussenvoegselBelastingplichtige: new FormControl<string | null>(null),
    achternaamBelastingplichtige: new FormControl<string | null>(null),
    omzetbelastingNummer: new FormControl<string | null>(null),
    naamContactPersoon: new FormControl<string | null>(null),
    telefoonnummer: new FormControl<string | null>(null),
    functie: new FormControl<string | null>(null),
    frequentie: new FormControl<BtwOverzichtFrequentie | null>(null),
    omzetbelastingNummerFE: new FormControl<string | null>(null),
    fiscaleEenheid: new FormControl<boolean | null>(null),
  });

  public frequentieFg = new FormGroup({
    status: new FormControl<string | null>(null),
  });

  readonly intermediary$: Observable<unknown> =
    this.algDataService.intermediary.pipe(
      tap(value => {
        this.beconNummer = value.beconId || '123456';
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly valueAddedTaxEntity$: Observable<ValueAddedTaxEntity> =
    this.dataService.valueAddedTaxEntity.pipe(
      filter((data): data is ValueAddedTaxEntity => !!data?.overzicht),
      tap(value => {
        this.updateForm(value);
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.intermediary$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.valueAddedTaxEntity$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    merge(
      this.btwOverzichtFg.valueChanges,
    ).pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.saveForm();
    });
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  private updateForm(value: ValueAddedTaxEntity): void {
    const overzicht = value.overzicht;

    if (!overzicht) {
      return;
    }

    this.btwNummer = overzicht.omzetbelastingNummer || '';

    this.btwOverzichtFg.patchValue(overzicht, {emitEvent: false});
    this.btwOverzichtFg.patchValue(
      {
        omzetbelastingNummer: this.btwNummer || null,
        beconNummer: this.beconNummer || overzicht.beconNummer || null,
      },
      {emitEvent: false}
    );

    this.setStatus(overzicht);
  }

  private setStatus(overzicht: BtwOverzicht): void {
    if (overzicht.frequentie === BtwOverzichtFrequentie.KWARTAAL) {
      this.frequentieFg.patchValue({status: 'Kwartaal'}, {emitEvent: false});
    } else if (overzicht.frequentie === BtwOverzichtFrequentie.MAAND) {
      this.frequentieFg.patchValue({status: 'Maandelijks'}, {emitEvent: false});
    } else if (overzicht.frequentie === BtwOverzichtFrequentie.JAAR) {
      this.frequentieFg.patchValue({status: 'Jaarlijks'}, {emitEvent: false});
    } else {
      this.frequentieFg.patchValue({status: null}, {emitEvent: false});
    }
  }

  saveForm(): void {
    this.btwOverzichtFg.updateValueAndValidity({emitEvent: false});

    const raw = this.btwOverzichtFg.getRawValue();

    this.dataService.patchValueAddedTaxEntity(current => {
      if (!current.overzicht) {
        return current;
      }

      return {
        ...current,
        overzicht: {
          ...current.overzicht,
          fiscaleEenheid: raw.fiscaleEenheid ?? undefined,
          omzetbelastingNummerFE: raw.omzetbelastingNummerFE ?? undefined,
          initialenBelastingplichtige: raw.initialenBelastingplichtige ?? undefined,
          tussenvoegselBelastingplichtige: raw.tussenvoegselBelastingplichtige ?? undefined,
          achternaamBelastingplichtige: raw.achternaamBelastingplichtige || undefined,
          omzetbelastingNummer: this.btwNummer || raw.omzetbelastingNummer || undefined,
          beconNummer: this.beconNummer || raw.beconNummer || undefined,
          naamContactPersoon: raw.naamContactPersoon ?? undefined,
          telefoonnummer: raw.telefoonnummer ?? undefined,
          functie: raw.functie ?? undefined,
        } as BtwOverzicht
      } as ValueAddedTaxEntity;
    });
  }
}
